/**
 * QweRty receiver.
 *
 * Feed it raw QR payload bytes. It verifies the packet CRC, tracks the
 * transfer id, learns the RaptorQ OTI from the manifest (or falls back to the
 * geometry implied by the first data packet), decodes with the rateless
 * decoder, and verifies the whole-payload hash before handing back a result.
 */

import { RaptorQDecoder } from '@qwerty/raptorq-wasm';
import { inflateSync } from 'fflate';
import { crc32c } from '../protocol/crc32c';
import {
  decodeManifest,
  parseTransportPacket,
  SYMBOL_MANIFEST,
  SYMBOL_RAPTORQ,
  type Manifest,
  type TransportHeader,
} from './protocol';

export type ReceiverPhase = 'idle' | 'receiving' | 'complete' | 'error';

export interface ReceiverProgress {
  phase: ReceiverPhase;
  transferId: number;
  isText: boolean;
  accepted: number;
  duplicates: number;
  rejected: number;
  needed: number;
  dataLength: number;
  qrVersion: number;
  message: string;
}

export interface ReceiverResult {
  transferId: number;
  isText: boolean;
  data: Uint8Array;
  text: string;
  filename: string;
  mime: string;
}

interface FallbackMeta {
  isText: boolean;
  compressed: boolean;
  dataLength: number;
  symbolSize: number;
}

export class QrReceiver {
  private decoder: RaptorQDecoder | null = null;
  private manifest: Manifest | null = null;
  private fallback: FallbackMeta | null = null;
  private transferId = 0;
  private seen = new Set<string>();
  private accepted = 0;
  private duplicates = 0;
  private rejected = 0;
  private completed = false;
  private result: ReceiverResult | null = null;
  private lastMessage = '';

  reset(): void {
    this.decoder?.free?.();
    this.decoder = null;
    this.manifest = null;
    this.fallback = null;
    this.transferId = 0;
    this.seen.clear();
    this.accepted = 0;
    this.duplicates = 0;
    this.rejected = 0;
    this.completed = false;
    this.result = null;
    this.lastMessage = '';
  }

  get currentResult(): ReceiverResult | null {
    return this.result;
  }

  get progress(): ReceiverProgress {
    const dataLength = this.manifest?.totalLength ?? this.fallback?.dataLength ?? 0;
    const symbolSize = this.manifest
      ? symbolSizeFromOti(this.manifest.oti)
      : this.fallback?.symbolSize ?? 0;
    const needed = symbolSize > 0 ? Math.max(1, Math.ceil(dataLength / symbolSize)) : 0;
    const isText = this.manifest?.isText ?? this.fallback?.isText ?? false;

    return {
      phase: this.completed ? 'complete' : this.decoder ? 'receiving' : 'idle',
      transferId: this.transferId,
      isText,
      accepted: this.accepted,
      duplicates: this.duplicates,
      rejected: this.rejected,
      needed,
      dataLength,
      qrVersion: this.manifest?.qrVersion ?? 0,
      message: this.lastMessage,
    };
  }

  /** Push one decoded QR payload. Returns the current progress. */
  push(bytes: Uint8Array): ReceiverProgress {
    let parsed;
    try {
      parsed = parseTransportPacket(bytes);
    } catch {
      this.rejected++;
      return this.progress;
    }

    const header = parsed.header;
    if (header.symbolIndex === SYMBOL_MANIFEST) {
      // A new manifest may start a fresh transfer even after completion.
      return this.acceptManifest(parsed.payload);
    }
    if (this.completed) return this.progress;
    if (header.symbolIndex !== SYMBOL_RAPTORQ) {
      this.rejected++;
      return this.progress;
    }
    return this.acceptData(header, parsed.payload);
  }

  private acceptManifest(payload: Uint8Array): ReceiverProgress {
    let manifest: Manifest;
    try {
      manifest = decodeManifest(payload);
    } catch {
      this.rejected++;
      return this.progress;
    }

    if (this.transferId !== manifest.transferId) {
      this.reset();
      this.transferId = manifest.transferId;
      this.manifest = manifest;
      this.decoder = RaptorQDecoder.fromOti(manifest.oti);
    } else {
      this.manifest = manifest;
      if (!this.decoder) {
        this.decoder = RaptorQDecoder.fromOti(manifest.oti);
      }
    }
    this.lastMessage = 'manifest ' + this.transferId;
    return this.progress;
  }

  private acceptData(header: TransportHeader, payload: Uint8Array): ReceiverProgress {
    if (this.transferId !== 0 && header.transferId !== this.transferId) {
      this.rejected++;
      return this.progress;
    }

    if (!this.decoder) {
      if (this.transferId === 0) {
        this.transferId = header.transferId;
        this.fallback = {
          isText: header.isText,
          compressed: header.compressed,
          dataLength: header.dataLength,
          symbolSize: payload.length - 4,
        };
        this.decoder = new RaptorQDecoder(header.dataLength, payload.length);
      } else {
        return this.progress;
      }
    }

    const key =
      payload[0]!.toString(36) +
      ':' + payload[1]!.toString(36) +
      ':' + payload[2]!.toString(36) +
      ':' + payload[3]!.toString(36);
    if (this.seen.has(key)) {
      this.duplicates++;
      return this.progress;
    }
    this.seen.add(key);
    this.accepted++;

    let decoded: Uint8Array | null = null;
    try {
      const value = this.decoder.push(payload);
      if (value) decoded = new Uint8Array(value as Uint8Array);
    } catch (err) {
      this.lastMessage = 'decode error: ' + (err instanceof Error ? err.message : String(err));
      return this.progress;
    }

    if (decoded) {
      this.finish(decoded);
    }
    return this.progress;
  }

  private finish(preprocessed: Uint8Array): void {
    if (this.manifest) {
      const hash = crc32c(preprocessed);
      if (hash !== (this.manifest.payloadHash >>> 0)) {
        this.lastMessage = 'payload hash mismatch (got 0x' + hash.toString(16) + ')';
        this.seen.clear();
        this.accepted = 0;
        return;
      }
    }

    const isText = this.manifest?.isText ?? this.fallback?.isText ?? false;
    const compressed = this.manifest?.compressed ?? this.fallback?.compressed ?? false;

    let finalData: Uint8Array;
    if (compressed) {
      try {
        finalData = inflateSync(preprocessed);
      } catch {
        this.lastMessage = 'decompression failed';
        return;
      }
    } else {
      finalData = preprocessed;
    }

    let filename = this.manifest?.filename ?? '';
    let mime = this.manifest?.mime ?? 'application/octet-stream';

    if (!isText) {
      try {
        if (finalData.length >= 2) {
          const filenameLen = finalData[0]!;
          if (finalData.length >= 2 + filenameLen) {
            const mimeLen = finalData[1 + filenameLen]!;
            const metaEnd = 2 + filenameLen + mimeLen;
            if (finalData.length >= metaEnd) {
              if (!filename) {
                filename = new TextDecoder().decode(finalData.subarray(1, 1 + filenameLen));
              }
              if (!this.manifest?.mime) {
                mime = new TextDecoder().decode(finalData.subarray(2 + filenameLen, metaEnd));
              }
              finalData = finalData.slice(metaEnd);
            }
          }
        }
      } catch {
        // fall through with raw data
      }
    }

    this.result = {
      transferId: this.transferId,
      isText,
      data: finalData,
      text: isText ? new TextDecoder().decode(finalData) : '',
      filename: filename || 'recovered-' + Date.now().toString(36),
      mime: mime || 'application/octet-stream',
    };
    this.completed = true;
    this.lastMessage = 'complete';
  }
}

function symbolSizeFromOti(oti: Uint8Array): number {
  if (oti.length < 4) return 0;
  return (oti[2]! << 8) | oti[3]!;
}
